import React, { useState } from "react";
import { Col, Container, Modal, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { scrollbarStyles, taskBgColor } from "../../../../../../utils/palette";
import { ItemContainer, ItemLabel, ItemValue } from "..";
import { HintText, LinkSpan } from "../../../../form/FormElements";
import { NATEXT } from "../../../../../constants";
import { ActionIconContainer } from "../../../../TableElements";
import { ScrollableDiv } from "../../../../../../components/Assets/Vehicles/MaintenanceSchedulePopup";
import styled from "styled-components";
import { formatNumber } from "../../../../../helpers";
import { TaskLogsPopup } from "../../../../form/ServiceTasksNew";

const formatElapsed = (minutes) => {
  if (!minutes) return "0m";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
};

const ServiceTasksSidePanel = ({ serviceTasks, rowData }) => {
  const { t } = useTranslation("common");
  const { currency } = useSelector((state) => state.units);
  const { woTimerMode } = useSelector((state) => state.user);
  const { templates } = useSelector((state) => state.servicesTasks);
  const users = useSelector((state) => state.users.list);

  const { parts_total, labor_total, tax, discount, total, template_id } =
    rowData;

    const grandTotal = +parts_total + +labor_total + +tax - +discount;

  const templateName = template_id
    ? templates?.find((template) => template.value === template_id)?.label
    : "";

  //labor + cost (without tax and discount)
  const totalCost = +labor_total + +parts_total;

  //To hold the value of the corresponding service task when `${count} parts` is clicked
  const [selectedTask, setSelectedTask] = useState([]);
  const [showPartsPopup, setShowPartsPopup] = useState(false);
  const [showLogsFor, setShowLogsFor] = useState(null);

  const handlePartsClick = (task) => {
    setSelectedTask(task);
    setShowPartsPopup(true);
  };
  return (
    <>
      {showPartsPopup && (
        <SidePanelPartsPopup
          showPopup={showPartsPopup}
          setShowPopup={setShowPartsPopup}
          serviceTask={selectedTask}
        />
      )}
      {showLogsFor &&
        (() => {
          const logsTask = serviceTasks?.find(
            (t) => t.service_task_detail_id === showLogsFor,
          );
          if (!logsTask) return null;
          return (
            <TaskLogsPopup
              task={logsTask}
              users={users}
              onHide={() => setShowLogsFor(null)}
            />
          );
        })()}

      <Row className="mt-2">
        <Col>
          <h5>{t("service_tasks")}</h5>
        </Col>
      </Row>

      {templateName && (
        <Row>
          <Col>
            <ItemContainer className="d-flex gap-2 align-items-end">
              <ItemLabel className="m-0">{`${t("template")}:`}</ItemLabel>
              <ItemValue>{templateName}</ItemValue>
            </ItemContainer>
          </Col>
        </Row>
      )}
      {serviceTasks?.map((task) => {
        return (
          <Row
            className="mx-0 my-2 pt-1"
            key={task.service_task_id}
            style={{
              background: taskBgColor,
              //padding: "4px",
              //margin: "0.25rem 0",
              borderRadius: "5px",
            }}
          >
            <Col>
              <Row>
                <Col>
                  <p style={{ fontSize: "16px" }}>{task.service_task_name}</p>
                  {(task?.noOfParts > 0 ||
                    ((task.initialTimerStatus === "completed" || woTimerMode === 2) &&
                      task.initialElapsed > 0)) && (
                    <div className="d-flex gap-3 align-items-center mt-1">
                      {task?.noOfParts > 0 && (
                        <LinkSpan onClick={() => handlePartsClick(task)}>
                          {t("count_parts", { count: task.noOfParts })}
                        </LinkSpan>
                      )}
                      {(task.initialTimerStatus === "completed" || woTimerMode === 2) &&
                        task.initialElapsed > 0 && (
                          <p style={{ fontSize: "12px" }}>
                            {formatElapsed(task.initialElapsed)}
                          </p>
                        )}
                    </div>
                  )}
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <ItemContainer>
                    <ItemLabel>{`${t("parts")} (${currency})`}</ItemLabel>
                    <ItemValue>
                      {task.partsCost
                        ? formatNumber(task.partsCost, 2)
                        : NATEXT}
                    </ItemValue>
                  </ItemContainer>
                </Col>

                <Col md={4}>
                  <ItemContainer>
                    <ItemLabel>{`${t("labor")} (${currency})`}</ItemLabel>
                    <ItemValue>
                      {task.laborCost
                        ? formatNumber(task.laborCost, 2)
                        : NATEXT}
                    </ItemValue>
                  </ItemContainer>
                </Col>

                <Col md={4}>
                  <ItemContainer>
                    <ItemLabel>{`${t("tc_tv")} (${currency})`}</ItemLabel>
                    <ItemValue>
                      {task.totalCost
                        ? formatNumber(task.totalCost, 2)
                        : NATEXT}
                    </ItemValue>
                  </ItemContainer>
                </Col>
              </Row>

              {(task.comments || (task.logs && task.logs.length > 0)) && (
                <hr style={{ margin: "4px 0" }} />
              )}

              {task.comments && (
                <Row>
                  <Col>
                    {/* <hr style={{ margin: "4px 0" }} /> */}
                    <div className="d-flex gap-2 align-items-start pb-1">
                      <i
                        className="fi fi-rr-memo-pad"
                        style={{
                          fontSize: "12px",
                          color: "#888",
                          position: "relative",
                          top: "2px",
                          flexShrink: 0,
                        }}
                      />
                      <p style={{ fontSize: "12px", color: "#555", margin: 0, wordBreak: "break-word", overflowWrap: "break-word", minWidth: 0 }}>
                        {task.comments}
                      </p>
                    </div>
                  </Col>
                </Row>
              )}
              {task.logs?.length > 0 && (
                <Row>
                  <Col>
                    <div className="pb-1">
                      <LinkSpan
                        style={{ fontSize: "12px" }}
                        onClick={() =>
                          setShowLogsFor(task.service_task_detail_id)
                        }
                      >
                        {`${t("task_log")} (${task.logs.length})`}
                      </LinkSpan>
                    </div>
                  </Col>
                </Row>
              )}
            </Col>
          </Row>
        );
      })}
      <Row
        style={{
          background: taskBgColor,
          padding: "4px",
          margin: "0.25rem 0",
          borderRadius: "5px",
        }}
      >
        <Col md={4}>
          <ItemContainer>
            <ItemLabel>{`${t("parts_total")}`}</ItemLabel>
            <ItemValue>
              {parts_total ? formatNumber(parts_total, 2) : 0}
            </ItemValue>
          </ItemContainer>
        </Col>
        <Col md={4}>
          <ItemContainer>
            <ItemLabel>{`${t("labor_total")}`}</ItemLabel>
            <ItemValue>
              {labor_total ? formatNumber(labor_total, 2) : 0}
            </ItemValue>
          </ItemContainer>
        </Col>
        <Col md={4}>
          <ItemContainer>
            <ItemLabel>{`${t("total")}`}</ItemLabel>
            <ItemValue>{totalCost ? formatNumber(totalCost, 2) : 0}</ItemValue>
          </ItemContainer>
        </Col>
      </Row>
      <Row className="d-flex justify-content-end">
        <Col>
          <ItemContainer
            className="d-flex gap-2 align-items-center justify-content-end"
            //style={{ marginRight: "8px" }}
          >
            <ItemLabel
              className="m-0"
              //style={{ minWidth: "90px" }}
            >
              {t("discount")}:
            </ItemLabel>
            <ItemValue>
              {+discount ? `${formatNumber(discount, 2)} ${currency}` : NATEXT}
            </ItemValue>
          </ItemContainer>
        </Col>
      </Row>
      <Row className="d-flex justify-content-end">
        <Col>
          <ItemContainer
            className="d-flex gap-2 align-items-center justify-content-end"
            //style={{ marginRight: "12px" }}
          >
            <ItemLabel
              className="m-0"
              //style={{ minWidth: "80px" }}
            >
              {t("tax")}:
            </ItemLabel>
            <ItemValue>
              {+tax ? `${formatNumber(tax, 2)} ${currency}` : NATEXT}
            </ItemValue>
          </ItemContainer>
        </Col>
      </Row>
      <Row className="d-flex justify-content-end">
        <Col>
          <ItemContainer className="d-flex gap-2 align-items-center justify-content-end">
            <ItemLabel
              className="m-0"
              //style={{ minWidth: "90px" }}
            >
              {t("grand_total")}:
            </ItemLabel>
            <ItemValue
              style={{
                fontWeight: 500,
                whiteSpace: "nowrap",
              }}
            >
              {+grandTotal ? `${formatNumber(+grandTotal, 2)} ${currency}` : NATEXT}
            </ItemValue>
          </ItemContainer>
        </Col>
      </Row>
    </>
  );
};

export default ServiceTasksSidePanel;

const SidePanelPartsPopup = ({ showPopup, setShowPopup, serviceTask }) => {
  const { t } = useTranslation("common");
  const { currency } = useSelector((state) => state.units);

  let totalPartsCost = 0;
  if (serviceTask?.batches?.length > 0) {
    totalPartsCost = serviceTask.batches.reduce(
      (prev, curr) => prev + curr?.totalCost,
      0,
    );
  }

  return (
    <Modal show={showPopup} onHide={() => setShowPopup(!showPopup)} size="lg" centered>
      <Modal.Header>
        {/* <p style={{ fontWeight: 500, fontSize: "16px" }}>
          Non Inspected Vehicles
        </p>
        <ActionIcon
          className="fi fi-rr-cross-small"
          onClick={() => setShowPopup(!showPopup)}
        ></ActionIcon> */}
        <h4>{serviceTask?.service_task_name}</h4>
        <div className="d-flex justify-content-end">
          <ActionIconContainer onClick={() => setShowPopup(!showPopup)}>
            <i className="fi fi-rr-cross-small" />
          </ActionIconContainer>
        </div>
      </Modal.Header>
      <Modal.Body>
        {/* <table style={{ width: "100%" }}>
          <thead>
            <tr>
              <th></th>
              <th>{t("consumed")}</th>
              <th>{`${t("cost_per_unit")} (${currency})`}</th>
              <th>{`${t("tc_tv")} (${currency})`}</th>
            </tr>
          </thead>
          <tbody>
            {serviceTask?.batches?.map((batch) => (
              <tr
                key={batch.parts_batch_id}
                style={{ background: taskBgColor, padding: "1rem" }}
              >
                <td>{batch.part_name}</td>
                <td>{batch.unitsConsumed}</td>
                <td>{batch.purchase_cost_per_unit || NATEXT}</td>
                <td>{batch.totalCost || NATEXT}</td>
              </tr>
            ))}
          </tbody>
        </table> */}
        <Container className="p-0 m-0">
          <Row>
            <Col className="m-0 p-0"></Col>
            <Col className="m-0 p-0">
              <span>{t("consumed")}</span>
            </Col>
            <Col className="m-0 p-0">
              <span>{`${t("cost_per_unit")} (${currency})`}</span>
            </Col>
            <Col className="m-0 p-0">
              <span>{`${t("tc_tv")} (${currency})`}</span>
            </Col>
          </Row>
          <ScrollableRow $height={"300px"}>
            <Col className="m-0 p-0">
              {serviceTask?.batches?.map((batch) => (
                <Row
                  key={batch.parts_batch_id}
                  style={{
                    background: taskBgColor,
                    padding: "1rem 0 1rem 1rem ",
                    margin: "0.5rem 0",
                  }}
                >
                  <Col className="m-0 p-0">
                    <p style={{ fontWeight: 500 }}>{batch.part_name}</p>
                    {batch.part_number && (
                      <HintText>{batch.part_number}</HintText>
                    )}
                  </Col>
                  <Col className="m-0 p-0">
                    <p>{batch.unitsConsumed}</p>
                  </Col>
                  <Col className="m-0 p-0">
                    <p>
                      {batch.purchase_cost_per_unit
                        ? formatNumber(batch.purchase_cost_per_unit)
                        : NATEXT}
                    </p>
                  </Col>
                  <Col className="m-0 p-0">
                    <p
                      style={{
                        paddingLeft: "1rem",
                        //paddingRight: "1rem",
                        //textAlign: "end",
                      }}
                    >
                      {batch.totalCost
                        ? formatNumber(batch.totalCost, 2)
                        : NATEXT}
                    </p>
                  </Col>
                </Row>
              ))}
            </Col>
          </ScrollableRow>
          <Row className="mt-2 mb-4">
            <Col>
              <div className="d-flex justify-content-end w-100">
                <div
                  className="d-flex gap-2"
                  style={{
                    position: "relative",
                    right: "35px",
                    //fontSize: "16px",
                  }}
                >
                  <span>{t("parts_total")}</span>
                  <p style={{ fontWeight: 500 }}>
                    {totalPartsCost
                      ? `${formatNumber(totalPartsCost, 2)} ${currency}`
                      : NATEXT}
                  </p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </Modal.Body>
    </Modal>
  );
};

//styles

const ScrollableRow = styled(Row)`
  margin: 0;
  padding: 0;
  overflow: auto;
  ${(p) => p.$height && `height: ${p.$height}`};
  max-height: 400px;
  ${scrollbarStyles}
`;
